<template>
    <main>
        <div class="contents">
            <div class="form">
                <h1 class="helvetica title1">Members Area</h1>
                <div class="body">
                    <p>If you would like to request an account in the EIC Members' Area please send an email to <a href="mailto:info@eic-federation.eu">info@eic-federation.eu</a> stating your name, email address and organisation. The EIC Privacy Policy applies to all users who apply or sign up for an account.</p>
                </div>
                <div class="form-container">
                    <NForm ref="loginForm" :model="data" :rules="rules" @submit.prevent="validateForm">
                        <NFormItem label="Email" path="email" size="large">
                            <NInput v-model:value="data.email" type="text" :input-props="{ type: 'email', autocomplete: 'email' }" placeholder="" required autofocus />
                        </NFormItem>
                        <NFormItem label="Password" path="password" size="large">
                            <NInput v-model:value="data.password" type="password" :input-props="{ autocomplete: 'current-password' }" placeholder="" required show-password-on="mousedown" />
                        </NFormItem>
                        <div class="buttonCont">
                            <NButton size="large" secondary attr-type="submit" :loading="inProgress">Login</NButton>
                        </div>
                    </NForm>
                    <NuxtLink :to="{ name: 'members-login-forgot' }" class="forgot">Forgot password?</NuxtLink>
                </div>
            </div>
            <div class="cover">
                <Image :image="linesImage" :width="900" :height="1100" imageClass="cover" />
            </div>
        </div>
        <WebsiteFooter />
    </main>
</template>

<script lang="ts" setup>
import type { FormInst, FormItemRule } from 'naive-ui';
import { NForm, NFormItem, NInput, useDialog, NButton } from 'naive-ui';

definePageMeta({ middleware: 'logged-user' })

const { validateEmail } = useValidation()
const { login } = useDirectusAuth()
const dialog = useDialog()

const loginForm = ref<FormInst>()
const data = reactive({
    email: '',
    password: ''
})

const linesImage = ref({
    id: '89dae3e6-fe71-4b6b-b871-b4502b5aa52f',
    width: 1200,
    height: 1600,
    title: '',
})

const rules = {
    email: {
        required: true,
        validator: (rule: FormItemRule, value: string) => {
            if (value == '') return Error('The email address is required')
            else {
                if (validateEmail(value)) return true
                else return Error('This is not a valid email address')
            }
        }
    },
    password: {
        required: true,
        message: 'This field is required'
    }
}

const { submit, inProgress, error } = useSubmit(
    () => {
        return login(data);
    },
    {
        onSuccess: async () => {
            await navigateTo({ name: 'members' })
        },
        onError: () => {
            dialog.error({
                class: 'errorDialog',
                title: 'Login was not successful',
                iconPlacement: 'top',
                positiveText: 'OK'
            })
        }
    }
)

const validateForm = (e: Event) => {
    e.preventDefault()
    loginForm.value?.validate((formErrors: any) => {
        if (!formErrors) { submit() }
        return
    })
}

useSeoMeta({
    title: 'Login | Members Area | EIC Federation',
    robots: 'noindex'
})
</script>

<style lang="scss" scoped>
main {
    padding-top: var(--size-padding-header);

    .contents {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: var(--size-margin-page);

        .form {
            padding-left: var(--size-margin-page);

            .body {
                margin-top: var(--size-gutter-double);
            }

            .form-container {
                margin-top: var(--size-gutter-double);
                
                border-top: var(--size-thickness) solid var(--color-primary);
                padding-top: var(--size-gutter-default);
                max-width: var(--text-maxwidth-body);
            }

            .forgot {
                color: var(--color-primary);
                text-decoration: underline;
                text-underline-offset: 2.5px;
                text-decoration-thickness: 1px;
            }
        }

        .cover {
            position: relative;
            padding-right: var(--size-margin-page);
            height: 100%;
            min-height: calc(100vh - var(--size-padding-header));
            background-color: #F8F8F6;
        }

        .buttonCont {
            padding: var(--size-gutter-default) 0;
        }
    }
}

@media screen and (max-width: 768px) {
    main {
        .contents {
            display: flex;
            flex-direction: column;
            gap: var(--size-gutter-sections);

            margin-bottom: calc(0px - var(--size-gutter-sections));

            .cover {
                height: 600px;
                min-height: 0;
            }
        }
    }
}
</style>